import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, FileText, Image, FileSpreadsheet, Trash2, Download, File } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import LoadingSpinner from "@/components/LoadingSpinner";
import { logError } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Anexos = () => {
  const [anexos, setAnexos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [novoAnexo, setNovoAnexo] = useState({
    nome: "",
    categoria: "Geral",
    arquivo: null,
  });

  useEffect(() => {
    carregarAnexos();
  }, []);

  const carregarAnexos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/anexos`);
      setAnexos(response.data);
    } catch (error) {
      logError("Erro ao carregar anexos:", error);
      toast.error("Erro ao carregar anexos");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "image/png",
        "image/jpeg",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error("Tipo de arquivo não permitido. Use PDF, PNG ou Excel.");
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error("Arquivo muito grande. Tamanho máximo: 10MB");
        return;
      }

      setNovoAnexo({ ...novoAnexo, arquivo: file, nome: file.name });
    }
  };

  const handleUpload = async () => {
    if (!novoAnexo.arquivo) {
      toast.error("Selecione um arquivo");
      return;
    }

    try {
      setUploading(true);

      // Converter arquivo para base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;

        const anexoData = {
          nome: novoAnexo.nome,
          tipo_arquivo: novoAnexo.arquivo.type,
          url: base64,
          tamanho: novoAnexo.arquivo.size,
          categoria: novoAnexo.categoria,
        };

        await axios.post(`${API}/anexos`, anexoData);
        toast.success("Arquivo enviado com sucesso!");
        setNovoAnexo({ nome: "", categoria: "Geral", arquivo: null });
        document.getElementById("file-input").value = "";
        carregarAnexos();
      };

      reader.readAsDataURL(novoAnexo.arquivo);
    } catch (error) {
      logError("Erro ao enviar arquivo:", error);
      toast.error("Erro ao enviar arquivo");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/anexos/${deleteDialog.id}`);
      toast.success("Anexo excluído com sucesso");
      setDeleteDialog({ open: false, id: null });
      carregarAnexos();
    } catch (error) {
      logError("Erro ao excluir anexo:", error);
      toast.error("Erro ao excluir anexo");
    }
  };

  const downloadAnexo = (anexo) => {
    const link = document.createElement("a");
    link.href = anexo.url;
    link.download = anexo.nome;
    link.click();
    toast.success("Download iniciado");
  };

  const getIcon = (tipo) => {
    if (tipo.includes("pdf")) return <FileText className="w-8 h-8 text-red-500" />;
    if (tipo.includes("image")) return <Image className="w-8 h-8 text-blue-500" />;
    if (tipo.includes("sheet") || tipo.includes("excel"))
      return <FileSpreadsheet className="w-8 h-8 text-green-500" />;
    return <File className="w-8 h-8 text-slate-500" />;
  };

  const formatarTamanho = (bytes) => {
    if (!bytes) return "N/A";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6" data-testid="anexos-container">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Anexos</h1>
        <p className="text-sm text-slate-500 mt-1">
          Gerenciamento de materiais e documentos da loja
        </p>
      </div>

      {/* Upload Form */}
      <Card className="bg-white border border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">
            Enviar Novo Arquivo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="file-input">Arquivo (PDF, PNG, Excel)</Label>
                <Input
                  id="file-input"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx"
                  onChange={handleFileChange}
                  data-testid="file-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nome-arquivo">Nome do Arquivo</Label>
                <Input
                  id="nome-arquivo"
                  value={novoAnexo.nome}
                  onChange={(e) => setNovoAnexo({ ...novoAnexo, nome: e.target.value })}
                  placeholder="Nome do arquivo"
                  data-testid="input-nome-arquivo"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria</Label>
                <Select
                  value={novoAnexo.categoria}
                  onValueChange={(value) => setNovoAnexo({ ...novoAnexo, categoria: value })}
                >
                  <SelectTrigger data-testid="select-categoria">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Geral">Geral</SelectItem>
                    <SelectItem value="Contratos">Contratos</SelectItem>
                    <SelectItem value="Fornecedores">Fornecedores</SelectItem>
                    <SelectItem value="Inventário">Inventário</SelectItem>
                    <SelectItem value="Relatórios">Relatórios</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              onClick={handleUpload}
              disabled={!novoAnexo.arquivo || uploading}
              data-testid="btn-upload"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Enviando..." : "Enviar Arquivo"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Anexos */}
      {loading ? (
        <LoadingSpinner message="Carregando anexos..." />
      ) : anexos.length === 0 ? (
        <Card className="bg-white border border-slate-200">
          <CardContent className="py-12 text-center">
            <p className="text-slate-500 mb-4">Nenhum anexo encontrado</p>
            <p className="text-sm text-slate-400">
              Use o formulário acima para enviar o primeiro arquivo
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {anexos.map((anexo) => (
            <Card
              key={anexo.id}
              className="bg-white border border-slate-200 hover:shadow-md transition-shadow"
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">{getIcon(anexo.tipo_arquivo)}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{anexo.nome}</h3>
                    <p className="text-xs text-slate-500 mt-1">{anexo.categoria}</p>
                    <p className="text-xs text-slate-400">
                      {formatarTamanho(anexo.tamanho)} •{" "}
                      {new Date(anexo.data_upload).toLocaleDateString("pt-BR")}
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadAnexo(anexo)}
                        data-testid={`btn-download-${anexo.id}`}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Baixar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteDialog({ open: true, id: anexo.id })}
                        data-testid={`btn-delete-${anexo.id}`}
                      >
                        <Trash2 className="w-3 h-3 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}
      >
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este anexo? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-button">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Anexos;
